import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { OffensePicker } from "../OffensePicker";
import { Field, inputCls, btnGhost, btnPrimary } from "../ui";
import { ujmsAddCharge, ujmsChargeAction } from "../../services/ujmsApi";

export const AMENDABLE = ["PROPOSED", "AUTHORIZED", "FILED", "AMENDED"];
export const newChargeForm = () => ({ mode: "add", statute_code: "", description: "", classification: "", offense_date: "", charge_id_code: "" });
export const amendChargeForm = (ch) => ({ mode: "amend", charge: ch, statute_code: ch.statute_code, description: ch.description, classification: ch.classification || "", charge_id_code: ch.charge_id_code || "", reason: "" });

/** Add a count / amend a count (append-only history) — shared by the case Charges tab and the Judge Edition bench pane. Charges come from the Statutory Authority catalog. */
export function ChargeFormDialog({ form: initial, onClose, caseId, caseType, onDone, testId = "charge-form-dialog" }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  if (!form) return null;
  const amend = form.mode === "amend";
  const pick = (o) => setForm({ ...form, statute_code: o.statute_code, description: o.description, classification: o.classification, charge_id_code: o.charge_id_code });
  const submit = async () => {
    setBusy(true);
    try {
      const body = { statute_code: form.statute_code, description: form.description, classification: form.classification, charge_id_code: form.charge_id_code || null };
      if (amend) {
        await ujmsChargeAction(form.charge.id, { action: "AMEND", ...body, reason: form.reason });
        toast.success(`Count ${form.charge.count_number} amended (history preserved)`);
      } else {
        await ujmsAddCharge(caseId, { ...body, offense_date: form.offense_date || null });
        toast.success("Count added to the case");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={testId}>
        <DialogHeader><DialogTitle>{amend ? `Amend Count ${form.charge?.count_number}` : "Add Count"}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <OffensePicker testId="charge-offense-picker" caseType={caseType} onPick={pick} />
          <div className="grid grid-cols-4 gap-2">
            <Field label="Charge ID"><input className={`${inputCls} font-mono`} value={form.charge_id_code || ""} onChange={(e) => setForm({ ...form, charge_id_code: e.target.value })} placeholder="from catalog" data-testid="charge-form-code" /></Field>
            <Field label="Statute" required><input className={inputCls} value={form.statute_code} onChange={(e) => setForm({ ...form, statute_code: e.target.value })} data-testid="charge-form-statute" /></Field>
            <Field label="Description" required><input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="charge-form-desc" /></Field>
            <Field label="Classification"><input className={inputCls} value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} data-testid="charge-form-class" /></Field>
          </div>
          {amend
            ? <Field label="Reason for amendment" required><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Amended per plea negotiations" data-testid="charge-form-reason" /></Field>
            : <Field label="Offense date"><input type="date" className={inputCls} value={form.offense_date} onChange={(e) => setForm({ ...form, offense_date: e.target.value })} data-testid="charge-form-offense-date" /></Field>}
          <div className="text-[10.5px] text-slate-500">Picking from the catalog carries the Charge ID, full sub-citation and charging language onto the count so the Information / Indictment print correctly.</div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy || !form.statute_code || !form.description || (amend && !form.reason)} data-testid="charge-form-submit" onClick={submit}>
            {amend ? "Amend Charge" : "Add Count"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
